import React, { useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductForm } from "./ProductForm";
import type { Category } from "@/hooks/useCategories";
import type { ArtistDetail } from "@/hooks/useArtistDetails";
import type { Product, CreateProductData, UpdateProductData } from "@/hooks/useProducts";

interface ProductControlsProps {
  categories: Category[];
  artistDetails: ArtistDetail[];
  createProduct: (data: CreateProductData) => Promise<Product | null>;
  updateProduct: (data: UpdateProductData) => Promise<Product | null>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  editingProduct: Product | null;
  onFormClose: () => void;
}

export function ProductControls({ 
  categories, 
  artistDetails, 
  createProduct, 
  updateProduct,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  editingProduct,
  onFormClose
}: ProductControlsProps) {
  
  const [isAddingNew, setIsAddingNew] = useState(false);
  
  // Form should be open when editingProduct is set OR when manually adding a new product
  const isFormOpen = editingProduct !== null || isAddingNew;

  const handleAddProduct = () => {
    // Set flag to indicate we're adding a new product
    setIsAddingNew(true);
  };

  const handleFormClose = () => {
    setIsAddingNew(false);
    onFormClose();
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search products by name, description, or artist..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.name}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button onClick={handleAddProduct}>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {isFormOpen && (
        <ProductForm
          categories={categories}
          artistDetails={artistDetails}
          editingProduct={editingProduct}
          onCreateProduct={createProduct}
          onUpdateProduct={updateProduct}
          onClose={handleFormClose}
        />
      )}
    </>
  );
}
